import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Search, Loader2, Plus, MapPin, Clock, Building2, ShieldCheck, Sparkles, MessagesSquare } from 'lucide-react';
import PoPShell from './PoPShell';
import AIScoreModal from './AIScoreModal';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popListOpportunities, popCreateOpportunity, popApply, ENGAGEMENT_ROUTES,
} from '../../lib/popApi';

const routeLabel = (id) => (ENGAGEMENT_ROUTES.find((r) => r.id === id) || {}).label || id;

const OppCard = ({ o, canApply, onApply, onAIScore, onMessage }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const doApply = async () => {
    setBusy(true); setMsg('');
    try {
      await onApply(o.id);
      setMsg('Applied ✓');
    } catch (e) {
      setMsg((e && e.response && e.response.data && e.response.data.detail) || 'Could not apply');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5" data-testid={`opp-card-${o.id}`}>
      <div className="flex items-start justify-between mb-2 gap-3">
        <div>
          <div className="text-sm font-serif font-semibold text-white">{o.title}</div>
          <div className="text-[11px] text-white/60 flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" /> {o.institution}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-300/10 border border-amber-300/30 px-2 py-0.5 rounded">{routeLabel(o.engagement_route)}</span>
      </div>
      <p className="text-xs text-white/70 leading-relaxed line-clamp-3 mb-3">{o.description}</p>
      <div className="flex items-center gap-3 text-[10px] text-white/50 mb-3">
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {o.term_months} months</span>
        {o.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.location}</span>}
      </div>
      {o.sectors && o.sectors.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {o.sectors.slice(0, 4).map((s) => (
            <span key={s} className="text-[9px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{s}</span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {canApply && (
          <button
            onClick={doApply} disabled={busy}
            data-testid={`opp-apply-${o.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-xs font-semibold"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Apply
          </button>
        )}
        {canApply && (
          <button
            onClick={() => onAIScore(o)}
            data-testid={`opp-ai-score-${o.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-emerald-400/30 text-emerald-200 hover:bg-emerald-400/10 text-xs font-semibold"
          >
            <Sparkles className="w-3 h-3" /> AI match score
          </button>
        )}
        {canApply && (
          <button
            onClick={() => onMessage(o)}
            data-testid={`opp-message-${o.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-xs font-semibold"
          >
            <MessagesSquare className="w-3 h-3" /> Message
          </button>
        )}
      </div>
      {msg && <div className="text-[10px] text-emerald-300 mt-2">{msg}</div>}
    </div>
  );
};

const PostOpportunityForm = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    institution: '', title: '', description: '', engagement_route: 'industry_funded',
    term_months: 12, location: '', sectors_csv: '', domains_csv: '',
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = {
        ...form,
        sectors: form.sectors_csv.split(',').map((s) => s.trim()).filter(Boolean),
        domains: form.domains_csv.split(',').map((s) => s.trim()).filter(Boolean),
        term_months: parseInt(form.term_months, 10) || 12,
      };
      delete payload.sectors_csv; delete payload.domains_csv;
      await popCreateOpportunity(payload);
      setOpen(false);
      setForm({ ...form, title: '', description: '' });
      onCreated();
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not post');
    } finally { setBusy(false); }
  };
  return (
    <div className="mb-6">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="opp-post-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-md">
          <Plus className="w-4 h-4" /> Post a PoP Opportunity
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 grid sm:grid-cols-2 gap-3">
          <input placeholder="Institution" value={form.institution} onChange={upd('institution')} data-testid="opp-form-institution"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Role title (e.g. PoP · Enterprise AI)" value={form.title} onChange={upd('title')} data-testid="opp-form-title"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <textarea placeholder="Description" value={form.description} onChange={upd('description')} data-testid="opp-form-desc" rows={4}
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <select value={form.engagement_route} onChange={upd('engagement_route')} data-testid="opp-form-route"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
            {ENGAGEMENT_ROUTES.map((r) => (<option key={r.id} value={r.id} className="bg-slate-900">{r.label}</option>))}
          </select>
          <input type="number" placeholder="Term (months)" value={form.term_months} onChange={upd('term_months')} data-testid="opp-form-months"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Location" value={form.location} onChange={upd('location')} data-testid="opp-form-loc"
            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          <input placeholder="Sectors (comma-separated)" value={form.sectors_csv} onChange={upd('sectors_csv')} data-testid="opp-form-sectors"
            className="col-span-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
          {err && <div className="text-xs text-rose-300 col-span-2">{err}</div>}
          <div className="col-span-2 flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="opp-form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-50 text-slate-900 text-sm font-semibold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Publish
            </button>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm text-white/70">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const PoPOpportunities = () => {
  const { user } = usePoPAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(''); const [route, setRoute] = useState('');
  const [scoreOpp, setScoreOpp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: 'open' };
      if (q) params.q = q;
      if (route) params.engagement_route = route;
      const res = await popListOpportunities(params);
      setItems(res.results || []);
    } catch (_e) { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, route]);

  const doApply = async (opp_id) => { await popApply({ opportunity_id: opp_id, cover_note: '' }); };
  const doMessage = (o) => {
    navigate('/pop/messages', { state: {
      peerUserId: o.posted_by_user_id,
      subject: `Re: ${o.title}`,
      contextType: 'opportunity',
      contextId: o.id,
      initialMessage: `Hi, I'm interested in the ${o.title} role at ${o.institution}. I'd love to discuss my fit.`,
    }});
  };
  const canApply = user && (user.role === 'industry_expert' || user.role === 'faculty');
  const canPost  = user && (user.role === 'university' || user.role === 'super_admin');

  return (
    <PoPShell activeTab="opps">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">OPPORTUNITIES</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-2">Open PoP roles across HEIs.</h1>
        <p className="text-sm text-white/60 max-w-2xl">Roles posted by universities and higher education institutions, aligned with the UGC framework.</p>
      </div>

      {canPost && <PostOpportunityForm onCreated={load} />}

      {/* Filters */}
      <div className="rounded-xl border border-white/10 bg-slate-900/40 p-4 mb-6 grid md:grid-cols-[1fr,240px] gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search role, description, institution…"
            data-testid="opp-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
        </div>
        <select value={route} onChange={(e) => setRoute(e.target.value)} data-testid="opp-route-filter"
          className="px-3 py-2.5 rounded-md bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300">
          <option value="">All engagement routes</option>
          {ENGAGEMENT_ROUTES.map((r) => (<option key={r.id} value={r.id} className="bg-slate-900">{r.label}</option>))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading opportunities…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-10 text-center">
          <Briefcase className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <div className="text-sm text-white/60">No open opportunities yet.</div>
          {!user && <div className="text-[11px] text-white/40 mt-2">Sign in and post the first role in your institution.</div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((o) => (<OppCard key={o.id} o={o} canApply={canApply} onApply={doApply} onAIScore={setScoreOpp} onMessage={doMessage} />))}
        </div>
      )}
      {user && !canApply && !canPost && (
        <div className="mt-6 text-[11px] text-white/50 italic flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-amber-300" /> Only Industry Experts and Faculty can apply. Universities can post roles.</div>
      )}
      {scoreOpp && (
        <AIScoreModal
          opportunityId={scoreOpp.id}
          opportunityTitle={`${scoreOpp.title} · ${scoreOpp.institution}`}
          onClose={() => setScoreOpp(null)}
        />
      )}
    </PoPShell>
  );
};

export default PoPOpportunities;
